import { Icon, Text, Tooltip } from '@wyz/ui';
import { uniq, uniqBy } from 'lodash-es';
import * as React from 'react';
import { Trans } from '~/common/context';
import { TranslationKeys } from '~/common/types';
import { DISCOUNT_TYPE_TRANSLATIONS } from '~/common/translators';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import {
  IDistributionExtraBonusVariant,
  IDistributionPricingPolicySeason,
  IDistributionPricingPolicySeasonCell,
  IPricingConfigurationType,
} from '~/purchase/types';

const {
  NO_VALUE,
  bonusSign,
  matchExtraBonusVariant,
  summarizeRange,
  tooltipClass,
  variantDiameterLabel,
} = DistributionPricingPolicyHelpers;

/** The row and the column a bonus restricted by nothing sits in. */
const EVERY_DIAMETER_ROW = 'ALL_DIAMETERS';
const EVERY_VEHICLE_TYPE_COLUMN = 'ALL_VEHICLE_TYPES';

/**
 * One amount of a divergent cell, placed in the tooltip grid by its diameter and vehicle type. The
 * column is keyed apart from its label: the purchase amounts name their vehicle type the way the
 * pricing API spells it, while a bonus carries the catalog one.
 */
type VariantDetail = {
  diameter: number | string;
  column: string;
  columnLabel: React.ReactNode;
  value: React.ReactNode;
};

/** An amount and the unit of its discount type, which not every discount type has. */
function Amount({
  value,
  discountType,
}: {
  value: number | string;
  discountType: number | null;
}) {
  const labelKey =
    null === discountType
      ? undefined
      : DISCOUNT_TYPE_TRANSLATIONS[discountType];

  return (
    <>
      {value}
      {labelKey && (
        <>
          {' '}
          <Trans i18nKey={labelKey} />
        </>
      )}
    </>
  );
}

export function PurchaseAmount({
  cell,
  season,
  printMode,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
}) {
  if (0 === cell.variants.length) {
    return (
      <span className='text-center'>
        {null === cell.purchaseValue || null === cell.discountType ? (
          NO_VALUE
        ) : (
          <Amount value={cell.purchaseValue} discountType={cell.discountType} />
        )}
      </span>
    );
  }

  const range = summarizeRange(
    cell.variants.map((variant) => variant.purchaseValue),
    cell.variants.map((variant) => variant.discountType),
  );

  return (
    <VariantTooltip
      season={season}
      details={cell.variants.map((variant) => ({
        diameter: variant.diameter,
        column: variant.vehicleType,
        columnLabel: variant.vehicleType,
        value: (
          <Amount
            value={variant.purchaseValue}
            discountType={variant.discountType}
          />
        ),
      }))}
      printMode={printMode}
    >
      <span className='text-center'>
        {null === range ? (
          NO_VALUE
        ) : (
          <Amount value={range.text} discountType={range.discountType} />
        )}
      </span>
    </VariantTooltip>
  );
}

export function TotalAmount({
  cell,
  season,
  extraBonusType,
  hqBonus,
  hqBonusType,
  printMode,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  extraBonusType: IPricingConfigurationType | null;
  hqBonus: number | null;
  hqBonusType: IPricingConfigurationType | null;
  printMode: boolean;
}) {
  const bonusRules = cell.extraBonusVariants ?? [];
  const computeTotal = (
    purchaseValue: number,
    discountType: number,
    extraBonus: number | null,
    bonusType: IPricingConfigurationType | null,
  ) => {
    const total = DistributionPricingPolicyHelpers.computeTotal(
      purchaseValue,
      discountType,
      extraBonus,
      bonusType,
      hqBonus,
      hqBonusType,
    );

    return null === total ? null : Number(total.toFixed(2));
  };

  if (0 === cell.variants.length) {
    const { purchaseValue, discountType } = cell;

    if (null === purchaseValue || null === discountType) {
      return NO_VALUE;
    }

    if (0 === bonusRules.length) {
      const total = computeTotal(
        purchaseValue,
        discountType,
        cell.extraBonus,
        extraBonusType,
      );

      return (
        <Text color='primary' weight='semi-bold'>
          {null === total ? (
            NO_VALUE
          ) : (
            <Amount value={total} discountType={discountType} />
          )}
        </Text>
      );
    }

    const coversEveryTyre = bonusRules.some(
      (rule) => null === rule.vehicleType && null === rule.diameterOperator,
    );
    const ruleTotals = bonusRules.map((rule) => ({
      diameter: variantDiameterLabel(rule) ?? EVERY_DIAMETER_ROW,
      column: rule.vehicleType?.key ?? EVERY_VEHICLE_TYPE_COLUMN,
      columnLabel: <BonusColumnLabel variant={rule} />,
      total: computeTotal(
        purchaseValue,
        discountType,
        rule.value,
        rule.bonusType,
      ),
      discountType,
    }));

    return (
      <TotalSummary
        season={season}
        totals={
          coversEveryTyre
            ? ruleTotals
            : [
                ...ruleTotals,
                {
                  diameter: EVERY_DIAMETER_ROW,
                  column: EVERY_VEHICLE_TYPE_COLUMN,
                  columnLabel: (
                    <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.every_vehicle_type' />
                  ),
                  total: computeTotal(purchaseValue, discountType, null, null),
                  discountType,
                },
              ]
        }
        printMode={printMode}
      />
    );
  }

  return (
    <TotalSummary
      season={season}
      totals={cell.variants.map((variant) => {
        // Bonuses restricted by criteria: each amount takes the rule it falls under, if any.
        const rule =
          0 === bonusRules.length
            ? { value: cell.extraBonus, bonusType: extraBonusType }
            : matchExtraBonusVariant(
                bonusRules,
                variant.diameter,
                variant.vehicleType,
              );

        return {
          diameter: variant.diameter,
          column: variant.vehicleType,
          columnLabel: variant.vehicleType,
          total: computeTotal(
            variant.purchaseValue,
            variant.discountType,
            rule?.value ?? null,
            rule?.bonusType ?? null,
          ),
          discountType: variant.discountType,
        };
      })}
      printMode={printMode}
    />
  );
}

/** The totals of a divergent cell: the range they span, gridded in a tooltip. */
function TotalSummary({
  totals,
  season,
  printMode,
}: {
  totals: (Omit<VariantDetail, 'value'> & {
    total: number | null;
    discountType: number;
  })[];
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
}) {
  const computed = totals.filter(
    (entry): entry is (typeof totals)[number] & { total: number } =>
      null !== entry.total,
  );

  if (0 === computed.length) {
    return NO_VALUE;
  }

  const range = summarizeRange(
    computed.map((entry) => entry.total),
    computed.map((entry) => entry.discountType),
  );

  return (
    <VariantTooltip
      season={season}
      details={computed.map((entry) => ({
        diameter: entry.diameter,
        column: entry.column,
        columnLabel: entry.columnLabel,
        value: <Amount value={entry.total} discountType={entry.discountType} />,
      }))}
      printMode={printMode}
    >
      <Text component='span' color='primary' weight='semi-bold'>
        {null === range ? (
          NO_VALUE
        ) : (
          <Amount value={range.text} discountType={range.discountType} />
        )}
      </Text>
    </VariantTooltip>
  );
}

/** The vehicle type a bonus rule targets, as the tooltip grid heads its column. */
function BonusColumnLabel({
  variant,
}: {
  variant: IDistributionExtraBonusVariant;
}) {
  return variant.vehicleType ? (
    <Trans
      i18nKey={variant.vehicleType.i18nKey as keyof TranslationKeys}
      // The catalog name is the label of last resort, when no loco key covers it.
      values={{ defaultValue: variant.vehicleType.name }}
    />
  ) : (
    <Trans i18nKey='app.distribution.pricing_policy.detail.advanced.every_vehicle_type' />
  );
}

/**
 * The bonuses behind a cell. A single bonus restricted by nothing shows its amount; as soon as
 * diameters or categories are targeted, the cell summarises the range they span and moves the rules
 * into the tooltip, the way the purchase amounts already do.
 */
export function ExtraBonusAmount({
  cell,
  season,
  printMode,
}: {
  cell: IDistributionPricingPolicySeasonCell;
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
}) {
  const variants = cell.extraBonusVariants ?? [];

  if (0 === variants.length) {
    return (
      <Text color='primary' weight='semi-bold'>
        {cell.extraBonus ?? NO_VALUE}
      </Text>
    );
  }

  // A bonus carries its own type rather than a discount type, and the sign already shows it.
  const range = summarizeRange(
    variants.map((variant) => variant.value),
    [],
  );

  return (
    <VariantTooltip
      season={season}
      details={variants.map((variant) => ({
        diameter: variantDiameterLabel(variant) ?? EVERY_DIAMETER_ROW,
        column: variant.vehicleType?.key ?? EVERY_VEHICLE_TYPE_COLUMN,
        columnLabel: <BonusColumnLabel variant={variant} />,
        value: `${variant.value} ${bonusSign(variant.bonusType)}`,
      }))}
      printMode={printMode}
    >
      <Text component='span' color='primary' weight='semi-bold'>
        {null === range ? NO_VALUE : range.text}
      </Text>
    </VariantTooltip>
  );
}

/**
 * The combinations behind a divergent cell read as a grid: one column per vehicle type, one row per
 * diameter. A combination the pricing policy does not cover leaves its cell empty.
 */
function VariantTooltip({
  details,
  season,
  printMode,
  children,
}: {
  details: VariantDetail[];
  season: IDistributionPricingPolicySeason;
  printMode: boolean;
  children?: React.ReactNode;
}) {
  const columns = uniqBy(details, (detail) => detail.column);
  const diameters = uniq(details.map((detail) => detail.diameter)).sort(
    (left, right) => (left > right ? 1 : -1),
  );
  const valueOf = (diameter: number | string, column: string) =>
    details.find(
      (detail) => detail.diameter === diameter && detail.column === column,
    )?.value;

  return printMode ? (
    children
  ) : (
    <Tooltip
      className={`extra-bonus-tooltip${tooltipClass(season)}`}
      label={
        <table className='extra-bonus-tooltip__variants'>
          <thead>
            <tr>
              <th />
              {columns.map((column) => (
                <th key={column.column}>{column.columnLabel}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {diameters.map((diameter) => (
              <tr key={diameter}>
                <th>
                  {EVERY_DIAMETER_ROW === diameter ? (
                    <Trans i18nKey='app.quote.pricing_policy.form.step1.all_diameters' />
                  ) : (
                    <Trans
                      i18nKey='app.distribution.pricing_policy.detail.diameter'
                      values={{ diameter: String(diameter) }}
                    />
                  )}
                </th>
                {columns.map((column) => (
                  <td key={column.column}>
                    {valueOf(diameter, column.column) ?? NO_VALUE}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <span className='extra-bonus-table__divergent d-flex align-items-center justify-content-center'>
        {children}
        <span className='extra-bonus-table__magnifier'>
          <Icon icon='search' size='md' style={{ transform: 'scale(0.58)' }} />
        </span>
      </span>
    </Tooltip>
  );
}
